import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ProposalModal = ({ onSend, onClose }) => {
  const [formData, setFormData] = useState({
    title: '',
    budget: '',
    timeline: '',
    deliverables: ['', '', '']
  });

  const handleDeliverableChange = (index, value) => {
    const newDeliverables = [...formData?.deliverables];
    newDeliverables[index] = value;
    setFormData({ ...formData, deliverables: newDeliverables });
  };

  const addDeliverable = () => {
    setFormData({
      ...formData,
      deliverables: [...formData?.deliverables, '']
    });
  };

  const removeDeliverable = (index) => {
    const newDeliverables = formData?.deliverables?.filter((_, i) => i !== index);
    setFormData({ ...formData, deliverables: newDeliverables });
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    const proposalData = {
      title: formData?.title,
      budget: parseFloat(formData?.budget),
      timeline: formData?.timeline,
      deliverables: formData?.deliverables?.filter(d => d?.trim() !== ''),
      status: 'pending'
    };

    onSend(proposalData);
  };

  const isValid = formData?.title && formData?.budget && formData?.timeline && 
    formData?.deliverables?.some(d => d?.trim() !== '');

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border sticky top-0 bg-card">
          <div className="flex items-center gap-2">
            <Icon name="FileText" size={20} className="text-primary" />
            <h3 className="text-lg font-semibold text-foreground">Create Proposal</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-muted rounded transition-smooth"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Proposal Title *
            </label>
            <Input
              type="text"
              value={formData?.title}
              onChange={(e) => setFormData({ ...formData, title: e?.target?.value })}
              placeholder="e.g., Instagram Campaign for New Single"
              required
            />
          </div>

          {/* Budget */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Budget (USD) *
            </label>
            <Input
              type="number"
              value={formData?.budget}
              onChange={(e) => setFormData({ ...formData, budget: e?.target?.value })}
              placeholder="1500"
              min="0"
              step="0.01"
              required
            />
          </div>

          {/* Timeline */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Timeline *
            </label>
            <Input
              type="text"
              value={formData?.timeline}
              onChange={(e) => setFormData({ ...formData, timeline: e?.target?.value })}
              placeholder="e.g., 2 weeks, 1 month"
              required
            />
          </div>

          {/* Deliverables */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Deliverables *
            </label>
            <div className="space-y-2">
              {formData?.deliverables?.map((deliverable, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    type="text"
                    value={deliverable}
                    onChange={(e) => handleDeliverableChange(index, e?.target?.value)}
                    placeholder={`Deliverable ${index + 1}`}
                    className="flex-1"
                  />
                  {formData?.deliverables?.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeDeliverable(index)}
                    >
                      <Icon name="Trash2" size={18} />
                    </Button>
                  )}
                </div>
              ))}
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addDeliverable}
              iconName="Plus"
              className="mt-2"
            >
              Add Deliverable
            </Button>
          </div>

          {/* Info */}
          <div className="bg-muted/50 border border-border rounded-lg p-3">
            <div className="flex gap-2">
              <Icon name="Info" size={16} className="text-primary mt-0.5 flex-shrink-0" />
              <p className="text-xs text-muted-foreground">
                This proposal will be sent as a special message card. The recipient can accept or decline it directly in the chat.
              </p>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex gap-2 p-4 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleSubmit}
            disabled={!isValid}
            iconName="Send"
            className="flex-1"
          >
            Send Proposal
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProposalModal;